import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, Trash2, Globe, Lock, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { publicGalleryApi, PublicLinkConfig } from '../../api/publicGallery';
import styles from './SharePublicLinkModal.module.css';

interface SharePublicLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  albumId: string;
}

const EXPIRES_OPTIONS = [
  { label: 'Never', value: '' },
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: '365 days', value: '365' },
];

export function SharePublicLinkModal({ isOpen, onClose, albumId }: SharePublicLinkModalProps) {
  const [linkConfig, setLinkConfig] = useState<PublicLinkConfig | null | undefined>(undefined); // undefined = not yet loaded
  const [loading, setLoading] = useState(false);

  // Create form state
  const [password, setPassword] = useState('');
  const [expiresIn, setExpiresIn] = useState('');
  const [creating, setCreating] = useState(false);

  // Copy state
  const [copied, setCopied] = useState(false);

  // Revoke state
  const [revokeConfirm, setRevokeConfirm] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const fetchLink = useCallback(async () => {
    if (!albumId) return;
    setLoading(true);
    try {
      const result = await publicGalleryApi.getMyLink(albumId);
      setLinkConfig(result);
    } catch {
      setLinkConfig(null);
    } finally {
      setLoading(false);
    }
  }, [albumId]);

  useEffect(() => {
    if (isOpen) {
      fetchLink();
      // Reset form
      setPassword('');
      setExpiresIn('');
      setRevokeConfirm(false);
      setCopied(false);
    }
  }, [isOpen, fetchLink]);

  const getPublicUrl = (token: string) => {
    const base = import.meta.env.PROD
      ? 'https://pixelmachha.com'
      : 'http://localhost:5173';
    return `${base}/p/${token}`;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      const opts: { expiresInDays?: number; password?: string } = {};
      if (expiresIn) opts.expiresInDays = parseInt(expiresIn);
      if (password.trim()) opts.password = password.trim();

      const config = await publicGalleryApi.createLink(albumId, opts);
      setLinkConfig(config);
      toast.success('Public link created!');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        // Link already exists — refetch
        await fetchLink();
        toast('A share link already exists for this album.');
      } else {
        toast.error('Failed to create link. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = () => {
    if (!linkConfig?.token) return;
    const url = getPublicUrl(linkConfig.token);
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleRevoke = async () => {
    if (!revokeConfirm) {
      setRevokeConfirm(true);
      return;
    }
    setRevoking(true);
    try {
      await publicGalleryApi.revokeLink(albumId);
      setLinkConfig(null);
      setRevokeConfirm(false);
      toast.success('Link revoked');
    } catch {
      toast.error('Failed to revoke link');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share publicly" size="md">
      <div className={styles['content']}>
        {/* Not yet loaded */}
        {linkConfig === undefined && loading && (
          <div className={styles['loading-placeholder']}>
            <div className={styles['loading-bar']} />
            <div className={styles['loading-bar']} style={{ width: '60%' }} />
          </div>
        )}

        {/* No link exists yet — show create form */}
        {linkConfig === null && !loading && (
          <form onSubmit={handleCreate}>
            <div className={styles['section-icon-row']}>
              <div className={styles['section-icon']}>
                <Globe size={20} />
              </div>
              <div>
                <p className={styles['section-title']}>Create a public share link</p>
                <p className={styles['section-desc']}>
                  Anyone with the link can view your album photos — no sign-in required.
                </p>
              </div>
            </div>

            {/* Optional PIN */}
            <div className={styles['field-group']}>
              <label className={styles['field-label']}>
                <Lock size={14} />
                PIN protection (optional)
              </label>
              <input
                type="password"
                placeholder="4–20 characters"
                className={styles['field-input']}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={4}
                maxLength={20}
              />
              <p className={styles['field-hint']}>
                Leave blank to allow access without a PIN.
              </p>
            </div>

            {/* Expiry */}
            <div className={styles['field-group']}>
              <label className={styles['field-label']}>
                <Calendar size={14} />
                Expires in
              </label>
              <select
                className={styles['field-select']}
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
              >
                {EXPIRES_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles['create-btn-wrap']}>
              <Button type="submit" variant="primary" loading={creating}>
                <Globe size={15} />
                Create link
              </Button>
            </div>
          </form>
        )}

        {/* Link exists — show it */}
        {linkConfig && !loading && (
          <div className={styles['link-section']}>
            {/* URL display + copy */}
            <div className={styles['url-row']}>
              <div className={styles['url-box']}>
                <Globe size={14} className={styles['url-icon']} />
                <span className={styles['url-text']}>{getPublicUrl(linkConfig.token)}</span>
              </div>
              <button
                className={styles['copy-btn']}
                onClick={handleCopy}
                aria-label="Copy link"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>

            {/* Badges */}
            <div className={styles['badge-row']}>
              {linkConfig.hasPassword && (
                <span className={styles['badge']}>
                  <Lock size={11} /> PIN protected
                </span>
              )}
              {linkConfig.expiresAt && (
                <span className={styles['badge']}>
                  <Calendar size={11} />
                  Expires {new Date(linkConfig.expiresAt).toLocaleDateString()}
                </span>
              )}
            </div>

            {/* Revoke */}
            <div className={styles['revoke-wrap']}>
              {revokeConfirm ? (
                <div className={styles['revoke-confirm-row']}>
                  <p className={styles['revoke-confirm-text']}>
                    Anyone with this link will immediately lose access. Are you sure?
                  </p>
                  <div className={styles['revoke-confirm-actions']}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRevokeConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={revoking}
                      onClick={handleRevoke}
                    >
                      Revoke link
                    </Button>
                  </div>
                </div>
              ) : (
                <button className={styles['revoke-btn']} onClick={handleRevoke}>
                  <Trash2 size={14} />
                  Revoke link
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
